import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Trash2,
  MapPin,
  User,
  Phone,
  Mail,
  FileText,
  Save,
  Send,
  Printer,
  CalendarCheck,
  Loader2,
  Building2,
  Eye,
  Map,
  ChevronUp,
  ChevronDown,
  CheckCircle,
  RefreshCw,
  CalendarDays,
  Repeat,
  ShieldCheck,
  Package,
  Sparkles,
  AlertCircle,
} from 'lucide-react';
import {
  calculateSuggestedOccurrences,
  formatSeasonEndShort,
} from '../utils/seasonalOccurrences';
import { collection, query, orderBy, getDocs, addDoc, doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { seedFirstRecurringJobIfNeeded } from '../utils/servicePlanSeeding';
import { activateServicePlanFromQuote } from '../utils/activateServicePlanFromQuote';
import { loadLeadForPrefill } from '../features/clients/api/loadLeadForPrefill';
import { db, auth } from '../firebase';
import { useCompany } from '../context/CompanyContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

/**
 * QuoteWorkspace - Manual quoting workspace for non-calculator scenarios
 * 
 * This is NOT a system-of-record for all quotes.
 * This is a manual quoting workspace for flat/manual pricing.
 * 
 * Key principles:
 * - Quote-first, not client-first
 * - Client/Lead linking is optional and happens at action time
 * - No calculator logic, no measurements
 * - Service Templates are Quotes-only
 * - Persists only on explicit action (Save/Send/Print/Book)
 * 
 * Mobile-first design:
 * - Single column layout on mobile
 * - Sticky bottom action bar with primary actions
 * - Collapsible summary card
 * - Large tap targets
 */

const QuoteWorkspace = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { companyId } = useCompany();
  const mapRef = useRef(null);
  const streetViewRef = useRef(null);

  // When QuoteWorkspace is launched from a Customer Profile (`?leadId=…`),
  // we operate in CONTEXTUAL mode: the lead already exists, so `persistQuote`
  // MUST reuse it instead of creating a duplicate via Firestore. Set by the
  // prefill effect once the lead has been loaded successfully.
  const leadIdFromUrl = searchParams.get('leadId') || null;
  const [contextLeadId, setContextLeadId] = useState(null);
  const hasPrefilledRef = useRef(false);
  
  const [actionInProgress, setActionInProgress] = useState(null);
  const [showMobileSummary, setShowMobileSummary] = useState(false);
  const [sentState, setSentState] = useState(null); // { quoteId, leadId, clientName, phone }
  const [showPlanConfirm, setShowPlanConfirm] = useState(false);
  // Synchronous in-flight lock — guards Approve against double-tap even before
  // React re-renders the disabled prop on the button. Prevents duplicate lead
  // creation, quote seeding and recurring job seeding over slow connections.
  const bookJobInFlightRef = useRef(false);
  
  // Client info (optional, inline)
  const [clientInfo, setClientInfo] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });

  // ----------------------------
  // 👤 Prefill from Customer Profile (`?leadId=…`)
  // ----------------------------
  // When QuoteWorkspace is launched from a profile, read the same lead doc
  // the profile renders from and prefill the inline client info. Stash the
  // leadId in `contextLeadId` so `persistQuote` reuses it instead of
  // creating a duplicate lead.
  //
  // TODO(company-field): When the lead schema gains a `company` field, the
  // loader will surface it and we should map it into `clientInfo` here.
  useEffect(() => {
    const loadLead = async () => {
      if (!leadIdFromUrl || !companyId || hasPrefilledRef.current) return;

      const res = await loadLeadForPrefill(companyId, leadIdFromUrl);
      if (!res.success || !res.lead) return;

      const lead = res.lead;
      hasPrefilledRef.current = true;
      setContextLeadId(lead.id);

      setClientInfo((prev) => ({
        ...prev,
        firstName: prev.firstName || lead.firstName,
        lastName: prev.lastName || lead.lastName,
        phone: prev.phone || lead.phone,
        email: prev.email || lead.email,
        address: prev.address || lead.address,
        city: prev.city || lead.city,
        state: prev.state || lead.state,
        zip: prev.zip || lead.zip,
      }));
    };

    loadLead();
  }, [leadIdFromUrl, companyId]);

  // Address lookup state
  const [addressSearch, setAddressSearch] = useState('');
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [mapView, setMapView] = useState('aerial'); // 'aerial' | 'street'
  const [mapInstance, setMapInstance] = useState(null);
  const [streetViewInstance, setStreetViewInstance] = useState(null);

  // Service templates
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  // Quote line items
  const [lineItems, setLineItems] = useState([]);

  // Service Plan (optional — fully manual, no auto-detection)
  const [recurringPlan, setRecurringPlan] = useState(null);

  // Service Programs (visit-sequence templates) — loaded once per company.
  // Used to bind a quote's recurring plan to an operational program so
  // `servicePlanTemplateId` is written automatically on lead creation.
  const [servicePrograms, setServicePrograms] = useState([]);

  // Load service programs on mount (best-effort — no UI failure if request fails).
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        const token = await currentUser.getIdToken();
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const res = await axios.get(`${backendUrl}/api/service-programs/`, {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setServicePrograms(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.warn('[QuoteWorkspace] Could not load service programs:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  // Select a service program and auto-fill label + interval. If programId is
  // empty/null, clears the binding but preserves whatever the operator typed.
  const selectServiceProgram = (programId) => {
    if (!programId) {
      setRecurringPlan((prev) => ({ ...(prev ?? {}), servicePlanTemplateId: null }));
      return;
    }
    const prog = servicePrograms.find((p) => p.id === programId);
    if (!prog) return;
    setRecurringPlan((prev) => ({
      ...(prev ?? {}),
      active: true,
      servicePlanTemplateId: programId,
      planLabel: prog.name || prev?.planLabel || '',
      interval: prog.suggestedIntervalDays || prev?.interval || 30,
      unit: 'days',
    }));
  };

  // Company billing / tax settings — loaded on mount (same pattern as RoofEstimator.jsx)
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxRate, setTaxRate]       = useState(0);

  // Prepay package — mirrors state shape used in QuoteSummary (Calculator path)
  const [prepayPackage, setPrepayPackage]           = useState(null);
  const [prepayPriceUserEdited, setPrepayPriceUserEdited] = useState(false);
  const prepayRef = useRef(null);

  // Handle prepay changes with auto-suggest for package price
  const handlePrepayChange = (updates) => {
    if (!updates.active && updates.active !== undefined) {
      // Toggle off — reset user-edited flag so next time auto-suggest works
      setPrepayPriceUserEdited(false);
    }
    if ('packagePrice' in updates) {
      // Operator manually typed a price — stop auto-overwriting
      setPrepayPriceUserEdited(true);
    }
    if ('visitCount' in updates && !prepayPriceUserEdited) {
      const perVisit = calculateTotal();
      if (updates.visitCount && perVisit > 0) {
        updates.packagePrice = parseFloat((updates.visitCount * perVisit).toFixed(2));
      }
    }
    setPrepayPackage(prev => ({ ...(prev ?? {}), ...updates }));
  };

  const handlePrepayPromptClick = () => {
    setPrepayPackage(prev => ({ ...(prev ?? {}), active: true }));
    setPrepayPriceUserEdited(false);
    setTimeout(() => prepayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 60);
  };

  // Load company billing settings on mount — sets taxEnabled + taxRate
  useEffect(() => {
    const loadBillingSettings = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        const token = await currentUser.getIdToken(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${backendUrl}/api/invoices/settings/deposit`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setTaxEnabled(!!data.taxEnabled);
        setTaxRate(typeof data.taxRate === 'number' ? data.taxRate : 0);
      } catch {
        // Non-blocking — fall back to no tax
      }
    };
    loadBillingSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Load service templates (Quotes-only)
  useEffect(() => {
    if (!companyId) return;

    const loadTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const templatesRef = collection(db, 'companies', companyId, 'serviceTemplates');
        const q = query(templatesRef, orderBy('name', 'asc'));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTemplates(data);
      } catch (error) {
        console.error('Failed to load service templates:', error);
      }
      setTemplatesLoading(false);
    };

    loadTemplates();
  }, [companyId]);

  // Initialize Google Maps for address lookup
  useEffect(() => {
    if (!window.google || !mapRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 41.2033, lng: -77.1945 }, // Default: PA center
      zoom: 18,
      mapTypeId: 'satellite',
      tilt: 0,
      disableDefaultUI: true,
      zoomControl: true,
    });

    setMapInstance(map);

    // Initialize address autocomplete
    const input = document.getElementById('address-search-input');
    if (input) {
      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          handlePlaceSelected(place, map);
        }
      });
    }

    return () => {
      // Cleanup
    };
  }, []);

  // Initialize Street View when map is ready and address is selected
  useEffect(() => {
    if (!mapInstance || !selectedAddress || !streetViewRef.current) return;

    const panorama = new window.google.maps.StreetViewPanorama(streetViewRef.current, {
      position: selectedAddress.location,
      pov: { heading: 0, pitch: 0 },
      disableDefaultUI: true,
      zoomControl: true,
    });

    setStreetViewInstance(panorama);
  }, [mapInstance, selectedAddress]);

  const handlePlaceSelected = (place, map) => {
    const location = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };

    // Parse address components
    let streetNumber = '';
    let streetName = '';
    let city = '';
    let state = '';
    let zip = '';

    place.address_components?.forEach((component) => {
      const types = component.types;
      if (types.includes('street_number')) streetNumber = component.long_name;
      if (types.includes('route')) streetName = component.long_name;
      if (types.includes('locality')) city = component.long_name;
      if (types.includes('administrative_area_level_1')) state = component.short_name;
      if (types.includes('postal_code')) zip = component.long_name;
    });

    const fullAddress = [streetNumber, streetName].filter(Boolean).join(' ');

    setSelectedAddress({
      formatted: place.formatted_address,
      location,
      streetNumber,
      streetName,
    });

    setClientInfo((prev) => ({
      ...prev,
      address: fullAddress,
      city,
      state,
      zip,
    }));

    setAddressSearch(place.formatted_address);

    // Center map on address
    map.setCenter(location);
    map.setZoom(19);

    // Add marker
    new window.google.maps.Marker({
      position: location,
      map,
      title: place.formatted_address,
    });
  };

  const handleAddFromTemplate = (template) => {
    const newItem = {
      id: `item-${Date.now()}`,
      templateId: template.id,
      name: template.name,
      description: template.description || '',
      price: template.defaultPrice || 0,
      tags: template.tags || [],
    };
    setLineItems([...lineItems, newItem]);
  };

  const handleAddCustomItem = () => {
    const newItem = {
      id: `item-${Date.now()}`,
      templateId: null,
      name: '',
      description: '',
      price: 0,
      tags: [],
    };
    setLineItems([...lineItems, newItem]);
  };

  const handleUpdateLineItem = (itemId, updates) => {
    setLineItems(lineItems.map((item) =>
      item.id === itemId ? { ...item, ...updates } : item
    ));
  };

  const handleRemoveLineItem = (itemId) => {
    setLineItems(lineItems.filter((item) => item.id !== itemId));
  };

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
  };

  /**
   * Slugify a service name into a system tag
   * e.g., "Roof Cleaning" → "roof-cleaning"
   */
  const slugifyServiceName = (name) => {
    if (!name) return null;
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-')          // Replace spaces with hyphens
      .replace(/-+/g, '-')           // Collapse multiple hyphens
      .replace(/^-|-$/g, '');        // Trim leading/trailing hyphens
  };

  /**
   * Collect all tags from line items
   * - Includes user-defined tags from item.tags
   * - Derives system tags from line item names (slugified)
   */
  const collectAllTags = () => {
    const allTags = new Set();
    lineItems.forEach((item) => {
      // Add user-defined tags
      (item.tags || []).forEach((tag) => allTags.add(tag));
      // Derive system tag from service name
      const serviceTag = slugifyServiceName(item.name);
      if (serviceTag) {
        allTags.add(serviceTag);
      }
    });
    return Array.from(allTags);
  };

  const validateQuote = () => {
    if (lineItems.length === 0) {
      toast.error('Please add at least one line item');
      return false;
    }

    const emptyItems = lineItems.filter((item) => !item.name.trim());
    if (emptyItems.length > 0) {
      toast.error('Please enter a name for all line items');
      return false;
    }

    return true;
  };

  /**
   * CENTRALIZED QUOTE PERSISTENCE
   * All actions MUST call this first and wait for success.
   * 
   * @param {string} status - 'draft' | 'sent' | 'approved'
   * @returns {Promise<{quoteId: string, leadId: string|null}>}
   * @throws {Error} if persistence fails
   */
  const persistQuote = async (status) => {
    // Validate companyId exists
    if (!companyId) {
      const error = new Error('Company ID is missing. Please refresh and try again.');
      console.error('[persistQuote] No companyId:', { companyId });
      throw error;
    }

    // Validate user is authenticated
    if (!auth.currentUser?.uid) {
      const error = new Error('You must be logged in to save quotes.');
      console.error('[persistQuote] No authenticated user');
      throw error;
    }

    const subtotal  = calculateTotal();
    const taxAmount = (taxEnabled && taxRate > 0)
      ? parseFloat((subtotal * taxRate / 100).toFixed(2))
      : 0;
    const grandTotal = subtotal + taxAmount;
    const tags = collectAllTags();
    const hasClientInfo = clientInfo.firstName.trim() || clientInfo.lastName.trim() || clientInfo.phone.trim();
    
    let leadId = null;

    // Step 1a (CONTEXTUAL launch): When this workspace was opened from an
    // existing Customer Profile (`?leadId=…`), reuse that lead directly.
    // Creating a new lead here would duplicate the customer record.
    if (contextLeadId) {
      leadId = contextLeadId;
      console.log('[persistQuote] Reusing context lead from profile:', leadId);
    }

    // Step 1b (STANDALONE launch): Create a new lead if client info is provided
    // and we're not already attached to one.
    if (!leadId && hasClientInfo) {
      const leadPayload = {
        // REQUIRED: createdBy must match auth.uid per Firestore rules
        createdBy: auth.currentUser.uid,
        firstName: clientInfo.firstName.trim() || null,
        lastName: clientInfo.lastName.trim() || null,
        phone: clientInfo.phone.trim() || null,
        email: clientInfo.email.trim() || null,
        address: {
          line1: clientInfo.address.trim() || '',
          city: clientInfo.city.trim() || '',
          state: clientInfo.state.trim() || '',
          zip: clientInfo.zip.trim() || '',
        },
        stage: status === 'approved' ? 'Approved' : 'Quoted',
        source: 'quote',
        tags: tags,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      console.log('[persistQuote] Creating lead:', { companyId, payload: leadPayload });
      
      try {
        const leadsRef = collection(db, 'companies', companyId, 'leads');
        const newLeadDoc = await addDoc(leadsRef, leadPayload);
        leadId = newLeadDoc.id;
        console.log('[persistQuote] Lead created:', leadId);
      } catch (leadError) {
        console.error('[persistQuote] Lead creation failed:', {
          error: leadError,
          code: leadError.code,
          message: leadError.message,
          path: `companies/${companyId}/leads`,
        });
        throw new Error(`Failed to create lead: ${leadError.message || 'Permission denied'}`);
      }
    }

    // Step 2: Build quote payload with required fields
    const quotePayload = {
      // Required fields - createdBy MUST match auth.uid per Firestore rules
      companyId: companyId,
      createdBy: auth.currentUser.uid,
      status: status,
      lineItems: lineItems.map((item) => ({
        name: item.name.trim(),
        description: item.description?.trim() || '',
        price: parseFloat(item.price) || 0,
        tags: item.tags || [],
      })),
      totals: {
        subtotal:  subtotal,
        tax:       taxAmount,
        grandTotal: grandTotal,
        discount:  0,
        taxRate:   taxRate,   // Stage 3: snapshot the rate used at quote time
      },
      // Optional fields
      leadId: leadId || null,
      source: 'manual',
      client: hasClientInfo ? {
        firstName: clientInfo.firstName.trim() || null,
        lastName: clientInfo.lastName.trim() || null,
        phone: clientInfo.phone.trim() || null,
        email: clientInfo.email.trim() || null,
        address: clientInfo.address.trim() ? {
          line1: clientInfo.address.trim(),
          city: clientInfo.city.trim(),
          state: clientInfo.state.trim(),
          zip: clientInfo.zip.trim(),
        } : null,
      } : null,
      tags: tags,
      pdfUrl: null,
      createdAt: serverTimestamp(),
      // Service Plan — stored at quote creation, activated on approval (handleBookJob)
      recurringPlan: recurringPlan?.active ? { ...recurringPlan } : null,
      // Prepay package — stored on quote; Season Package card in QuoteDetail activates the charge flow
      prepayPackage: (prepayPackage?.active && prepayPackage?.visitCount > 0 && prepayPackage?.packagePrice > 0)
        ? { ...prepayPackage }
        : null,
    };

    console.log('[persistQuote] Quote payload createdBy:', quotePayload.createdBy);
    console.log('[persistQuote] Current auth.uid:', auth.currentUser?.uid);

    // Step 3: Write quote to top-level company collection
    // Architecture: Quotes are ALWAYS at companies/{companyId}/quotes/{quoteId}
    // Leads/clients are referenced via leadId/clientId fields, not nested paths
    const quotePath = `companies/${companyId}/quotes`;
    const quotesRef = collection(db, 'companies', companyId, 'quotes');

    console.log('[persistQuote] Saving quote:', { path: quotePath, payload: quotePayload });

    try {
      // Use doc() to pre-generate the ID so we can include quoteId in the payload.
      // This enables collection-group lookups on the quoteId field (public quote page).
      const newQuoteRef = doc(quotesRef);
      const newQuoteId = newQuoteRef.id;
      await setDoc(newQuoteRef, { ...quotePayload, quoteId: newQuoteId });
      console.log('[persistQuote] Quote saved:', { quoteId: newQuoteId, leadId });
      
      // Generate PDF in background (non-blocking)
      generatePdfAsync(companyId, newQuoteId);
      
      return { quoteId: newQuoteId, leadId };
    } catch (quoteError) {
      console.error('[persistQuote] Quote creation failed:', {
        error: quoteError,
        code: quoteError.code,
        message: quoteError.message,
        path: quotePath,
        payload: quotePayload,
      });
      throw new Error(`Failed to save quote: ${quoteError.message || 'Permission denied'}`);
    }
  };

  /**
   * Generate PDF for a quote (non-blocking)
   * Runs in background after quote is saved
   */
  const generatePdfAsync = async (companyId, quoteId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const response = await fetch(`${backendUrl}/api/quotes/pdf/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId, quoteId }),
      });
      
      const data = await response.json();
      if (data.success) {
        console.log('[generatePdfAsync] PDF generated:', data.pdfUrl);
      } else {
        console.warn('[generatePdfAsync] PDF generation failed:', data.message);
        // Non-blocking - user can manually regenerate later
      }
    } catch (error) {
      console.warn('[generatePdfAsync] PDF generation error:', error);
      // Non-blocking - quote save already succeeded
    }
  };

  /**
   * Action: Save Draft
   * Persists quote with status 'draft'
   */
  const handleSaveDraft = async () => {
    if (!validateQuote()) return;

    setActionInProgress('save');
    try {
      const { leadId, quoteId } = await persistQuote('draft');
      console.log('[handleSaveDraft] Success:', { quoteId, leadId });
      toast.success('Quote saved as draft');
      if (leadId) {
        navigate(`/leads/${leadId}`);
      } else {
        navigate('/quotes');
      }
    } catch (error) {
      console.error('[handleSaveDraft] Error:', error);
      toast.error(error.message || 'Failed to save quote');
    } finally {
      setActionInProgress(null);
    }
  };

  /**
   * Action: Send via SMS
   * 1. Persist quote first (creates the company-scoped document)
   * 2. Call /api/quotes/{quoteId}/send-sms — no second quote is created
   */
  const handleSendSMS = async () => {
    if (!validateQuote()) return;

    if (!clientInfo.phone.trim()) {
      toast.error('Phone number is required to send SMS');
      return;
    }

    setActionInProgress('sms');
    try {
      // Step 1: Persist quote — creates companies/{companyId}/quotes/{quoteId}
      const { leadId, quoteId } = await persistQuote('sent');
      console.log('[handleSendSMS] Quote persisted:', { quoteId, leadId });

      // Step 2: Send SMS using the existing quote — no duplicate document created
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) {
        throw new Error('Authentication token expired. Please refresh.');
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      await axios.post(
        `${backendUrl}/api/quotes/${quoteId}/send-sms`,
        { companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const clientName = [clientInfo.firstName, clientInfo.lastName].filter(Boolean).join(' ').trim();
      setSentState({ quoteId, leadId, clientName: clientName || clientInfo.phone, phone: clientInfo.phone });
    } catch (error) {
      console.error('[handleSendSMS] Error:', error);
      const message = error.response?.data?.detail || error.message || 'Failed to send SMS';
      toast.error(message);
    } finally {
      setActionInProgress(null);
    }
  };

  /**
   * Action: Print Quote
   * Renders a proper print view with quote content
   */
  const handlePrint = () => {
    if (!validateQuote()) return;

    // Create a printable version of the quote
    const clientName = [clientInfo.firstName, clientInfo.lastName].filter(Boolean).join(' ') || 'Customer';
    const total = calculateTotal();
    const date = new Date().toLocaleDateString();

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Quote - ${clientName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #333; margin-bottom: 8px; }
          .header { border-bottom: 2px solid #f26b1d; padding-bottom: 20px; margin-bottom: 30px; }
          .date { color: #666; font-size: 14px; }
          .client-section { margin-bottom: 30px; }
          .client-section h3 { color: #666; font-size: 12px; text-transform: uppercase; margin-bottom: 8px; }
          .client-name { font-size: 18px; font-weight: bold; }
          .client-details { color: #666; font-size: 14px; line-height: 1.6; }
          .items-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          .items-table th { background: #f5f5f5; padding: 12px; text-align: left; border-bottom: 2px solid #ddd; }
          .items-table td { padding: 12px; border-bottom: 1px solid #eee; }
          .items-table .price { text-align: right; }
          .total-section { text-align: right; padding: 20px 0; border-top: 2px solid #333; }
          .total-label { font-size: 16px; color: #666; }
          .total-amount { font-size: 24px; font-weight: bold; color: #333; }
          .footer { margin-top: 50px; text-align: center; color: #999; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Quote</h1>
          <div class="date">Date: ${date}</div>
        </div>
        
        ${clientName !== 'Customer' ? `
        <div class="client-section">
          <h3>Prepared For</h3>
          <div class="client-name">${clientName}</div>
          <div class="client-details">
            ${clientInfo.phone ? `<div>${clientInfo.phone}</div>` : ''}
            ${clientInfo.email ? `<div>${clientInfo.email}</div>` : ''}
            ${clientInfo.address ? `<div>${clientInfo.address}${clientInfo.city ? `, ${clientInfo.city}` : ''}${clientInfo.state ? ` ${clientInfo.state}` : ''} ${clientInfo.zip || ''}</div>` : ''}
          </div>
        </div>
        ` : ''}
        
        <table class="items-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Description</th>
              <th class="price">Price</th>
            </tr>
          </thead>
          <tbody>
            ${lineItems.map(item => `
              <tr>
                <td><strong>${item.name}</strong></td>
                <td>${item.description || '-'}</td>
                <td class="price">$${parseFloat(item.price || 0).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="total-section">
          <span class="total-label">Total: </span>
          <span class="total-amount">$${total.toFixed(2)}</span>
        </div>
        
        <div class="footer">
          <p>Thank you for your business!</p>
        </div>
      </body>
      </html>
    `;

    // Open print window with actual content
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    } else {
      toast.error('Please allow popups to print the quote');
    }
  };

  /**
   * Action: Approve Quote
   * ALWAYS creates a lead (required for booking)
   * Creates lead with stage 'Approved' (quote accepted, awaiting scheduling)
   */
  // ── Gate: show plan confirmation if recurringPlan is active ─────────────────
  const handleBookJob = () => {
    if (!validateQuote()) return;
    const hasClientInfo = clientInfo.firstName.trim() || clientInfo.lastName.trim() || clientInfo.phone.trim();
    if (!hasClientInfo) {
      toast.error('Please enter client name or phone to book a job');
      return;
    }
    if (recurringPlan?.active && !recurringPlan?.interval) {
      toast.error('Please select a visit frequency for the service plan.');
      return;
    }
    // If plan is active, show confirmation dialog before committing
    if (recurringPlan?.active) {
      setShowPlanConfirm(true);
      return;
    }
    executeBookJob();
  };

  // ── Core booking logic (called directly or after plan confirmation) ──────────
  const executeBookJob = async () => {
    // Synchronous re-entry guard. React's `disabled` prop on the Approve CTA
    // trails state by a render tick, so a fast double-tap can slip two calls
    // through. The ref is set BEFORE any await so the second call bails out.
    if (bookJobInFlightRef.current) {
      console.log('[handleBookJob] Ignored duplicate approve — booking already in progress');
      return;
    }
    bookJobInFlightRef.current = true;

    if (!validateQuote()) {
      bookJobInFlightRef.current = false;
      return;
    }

    // Approve Quote requires at least some client info to create a lead
    const hasClientInfo = clientInfo.firstName.trim() || clientInfo.lastName.trim() || clientInfo.phone.trim();
    if (!hasClientInfo) {
      toast.error('Please enter client name or phone to book a job');
      bookJobInFlightRef.current = false;
      return;
    }

    // Validate service plan — require an interval before saving
    if (recurringPlan?.active && !recurringPlan?.interval) {
      toast.error('Please select a visit frequency for the service plan.');
      bookJobInFlightRef.current = false;
      return;
    }

    setActionInProgress('book');
    try {
      const { leadId, quoteId } = await persistQuote('approved');
      console.log('[handleBookJob] Success:', { quoteId, leadId });
      
      if (!leadId) {
        // This shouldn't happen since we validated client info above
        console.warn('[handleBookJob] No lead created despite client info');
      }

      // ── Service Plan activation ───────────────────────────────────────────
      // Sprint 304 (Phase 2 closure): single-writer path. Route quote
      // activation through the shared helper which POSTs to
      // `/api/leads/{id}/enrollments`. Enrollment is authoritative;
      // legacy `lead.recurrence` mirror is populated server-side when
      // no primary enrollment exists. Guard on `already_active` still
      // applies (helper reads the lead and bails).
      if (recurringPlan?.active && leadId) {
        try {
          const result = await activateServicePlanFromQuote(
            companyId, leadId, recurringPlan,
            { quoteId, taxEnabled, taxRate },   // snapshot recurring tax terms
          );
          if (result.activated) {
            console.log('[handleBookJob] Enrollment activated', {
              leadId, enrollmentId: result.enrollmentId,
            });
          } else if (result.reason === 'already_active') {
            console.log('[handleBookJob] Recurrence write skipped — lead already has active plan', { leadId });
          }
        } catch (planErr) {
          console.error('[handleBookJob] Service plan setup failed (non-fatal):', planErr);
          // Non-fatal — quote and lead are already saved; user can activate plan from Lead Detail
          toast.error('Job booked, but service plan setup failed. Activate it from the lead profile.');
        }
      }

      // NOTE: Prepay package activation for the Manual Quote path is not yet implemented.
      // When added, write `prepayPackage.totalCoveredVisits` alongside `remainingCoveredVisits`
      // so LeadServicePlanCard can display "X of Y visits remaining".

      // Show a plan-aware success message
      if (recurringPlan?.active) {
        toast.success(
          `Quote approved — ${recurringPlan.planLabel || 'Service plan'} activated`,
          { description: `Every ${recurringPlan.interval} days${recurringPlan.totalOccurrences ? ` · ${recurringPlan.totalOccurrences} visits` : ' · ongoing'}` }
        );
      } else {
        toast.success('Job booked successfully');
      }

      if (leadId) {
        navigate(`/leads/${leadId}`);
      } else {
        navigate('/quotes');
      }
    } catch (error) {
      console.error('[handleBookJob] Error:', error);
      toast.error(error.message || 'Failed to book job');
    } finally {
      setActionInProgress(null);
      bookJobInFlightRef.current = false;
    }
  };

  const total      = calculateTotal(); // subtotal (sum of line items)
  const taxAmount  = (taxEnabled && taxRate > 0)
    ? parseFloat((total * taxRate / 100).toFixed(2))
    : 0;
  const grandTotal = total + taxAmount;

  // ── Quote Sent Confirmation Screen ─────────────────────────────────────────
  if (sentState) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-green-200 shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Quote Sent!</h2>
          <p className="text-gray-500 mb-1 text-sm">
            Your quote was delivered to
          </p>
          <p className="text-gray-900 font-semibold text-lg mb-1">
            {sentState.clientName}
          </p>
          {sentState.phone && sentState.clientName !== sentState.phone && (
            <p className="text-gray-400 text-sm mb-6">{sentState.phone}</p>
          )}
          {!sentState.phone || sentState.clientName === sentState.phone ? (
            <div className="mb-6" />
          ) : null}
          <p className="text-xs text-gray-400 mb-8 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
            They'll receive an SMS with a link to view and accept the quote. You'll be notified when they respond.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate(sentState.leadId ? `/leads/${sentState.leadId}` : '/quotes')}
              data-testid="view-quote-btn"
            >
              View Quote
            </Button>
            <Button
              className="flex-1 text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              onClick={() => {
                setSentState(null);
                setClientInfo({ firstName: '', lastName: '', phone: '', email: '', address: '', city: '', state: '', zip: '' });
                setLineItems([]);
                setAddressSearch('');
                setSelectedAddress(null);
              }}
              data-testid="send-another-quote-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Send Another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-6">
      {/* Header - Mobile optimized */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/quotes')}
                data-testid="back-to-quotes-btn"
                className="px-2 sm:px-3"
              >
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Back to Quotes</span>
              </Button>
              <div className="h-6 w-px bg-gray-200 hidden sm:block" />
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">New Quote</h1>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="quote-header-total">
              ${grandTotal.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Single column on mobile, two columns on desktop */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="grid lg:grid-cols-[1fr,400px] gap-4 sm:gap-6">
          {/* Left Column - Quote Details */}
          <div className="space-y-6">
            {/* Client Info (Optional, Inline) */}
            <Card className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <User className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Client Info</h2>
                  <p className="text-xs sm:text-sm text-gray-500">Optional - can be added later</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="client-firstName" className="text-sm">First Name</Label>
                  <Input
                    id="client-firstName"
                    value={clientInfo.firstName}
                    onChange={(e) => setClientInfo({ ...clientInfo, firstName: e.target.value })}
                    placeholder="First name"
                    data-testid="client-firstName-input"
                    className="h-11 sm:h-10 text-base sm:text-sm"
                  />
                </div>
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="client-lastName" className="text-sm">Last Name</Label>
                  <Input
                    id="client-lastName"
                    value={clientInfo.lastName}
                    onChange={(e) => setClientInfo({ ...clientInfo, lastName: e.target.value })}
                    placeholder="Last name"
                    data-testid="client-lastName-input"
                    className="h-11 sm:h-10 text-base sm:text-sm"
                  />
                </div>
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="client-phone" className="text-sm">Phone</Label>
                  <Input
                    id="client-phone"
                    type="tel"
                    value={clientInfo.phone}
                    onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="client-phone-input"
                    className="h-11 sm:h-10 text-base sm:text-sm"
                  />
                </div>
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="client-email" className="text-sm">Email</Label>
                  <Input
                    id="client-email"
                    type="email"
                    value={clientInfo.email}
                    onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                    placeholder="email@example.com"
                    data-testid="client-email-input"
                    className="h-11 sm:h-10 text-base sm:text-sm"
                  />
                </div>
              </div>
            </Card>

            {/* Address Lookup with Map Preview */}
            <Card className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <MapPin className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Property Address</h2>
                  <p className="text-xs sm:text-sm text-gray-500">Search address for context view</p>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4">
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="address-search-input" className="text-sm">Address Search</Label>
                  <Input
                    id="address-search-input"
                    value={addressSearch}
                    onChange={(e) => setAddressSearch(e.target.value)}
                    placeholder="Search address..."
                    data-testid="address-search-input"
                    className="h-11 sm:h-10 text-base sm:text-sm"
                  />
                </div>

                {/* View Toggle - Compact on mobile */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={mapView === 'aerial' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMapView('aerial')}
                    data-testid="aerial-view-btn"
                    className="flex-1 sm:flex-none h-10 sm:h-9"
                  >
                    <Map className="w-4 h-4 sm:mr-2" />
                    <span className="ml-1.5 sm:ml-0">Aerial</span>
                  </Button>
                  <Button
                    type="button"
                    variant={mapView === 'street' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMapView('street')}
                    disabled={!selectedAddress}
                    data-testid="street-view-btn"
                    className="flex-1 sm:flex-none h-10 sm:h-9"
                  >
                    <Eye className="w-4 h-4 sm:mr-2" />
                    <span className="ml-1.5 sm:ml-0">Street</span>
                  </Button>
                </div>

                {/* Map Container - Smaller on mobile */}
                <div className="relative rounded-lg overflow-hidden border h-[180px] sm:h-[250px]">
                  <div
                    ref={mapRef}
                    className={`absolute inset-0 ${mapView === 'aerial' ? 'block' : 'hidden'}`}
                    data-testid="aerial-map"
                  />
                  <div
                    ref={streetViewRef}
                    className={`absolute inset-0 ${mapView === 'street' ? 'block' : 'hidden'}`}
                    data-testid="street-view"
                  />
                  {!selectedAddress && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-500">
                      <div className="text-center px-4">
                        <Building2 className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-xs sm:text-sm">Search an address to view property</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Manual Address Fields (shown when address selected) */}
                {selectedAddress && (
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <Input
                      value={clientInfo.address}
                      onChange={(e) => setClientInfo({ ...clientInfo, address: e.target.value })}
                      placeholder="Street"
                      className="col-span-2 h-11 sm:h-10"
                    />
                    <Input
                      value={clientInfo.city}
                      onChange={(e) => setClientInfo({ ...clientInfo, city: e.target.value })}
                      placeholder="City"
                      className="h-11 sm:h-10"
                    />
                    <div className="flex gap-2">
                      <Input
                        value={clientInfo.state}
                        onChange={(e) => setClientInfo({ ...clientInfo, state: e.target.value })}
                        placeholder="ST"
                        className="w-16 h-11 sm:h-10"
                      />
                      <Input
                        value={clientInfo.zip}
                        onChange={(e) => setClientInfo({ ...clientInfo, zip: e.target.value })}
                        placeholder="ZIP"
                        className="flex-1 h-11 sm:h-10"
                      />
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Line Items */}
            <Card className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <FileText className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">Line Items</h2>
                    <p className="text-xs sm:text-sm text-gray-500">Add services and pricing</p>
                  </div>
                </div>
              </div>

              {/* Service Templates (Quotes-only) */}
              {templates.length > 0 && (
                <div className="mb-4">
                  <Label className="text-xs text-gray-500 uppercase tracking-wide">Quick Add from Templates</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {templates.map((template) => (
                      <Button
                        key={template.id}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddFromTemplate(template)}
                        data-testid={`template-${template.id}`}
                        className="h-10 sm:h-9"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        {template.name}
                        {template.defaultPrice > 0 && (
                          <span className="ml-1 text-gray-500">${template.defaultPrice}</span>
                        )}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <Button
                type="button"
                variant="outline"
                onClick={handleAddCustomItem}
                className="w-full mb-4 h-11 sm:h-10"
                data-testid="add-custom-item-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Custom Line Item
              </Button>

              {/* Line Items List */}
              {lineItems.length === 0 ? (
                <div className="text-center py-6 sm:py-8 text-gray-500 border-2 border-dashed rounded-lg">
                  <FileText className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm sm:text-base">No line items yet</p>
                  <p className="text-xs sm:text-sm">Add from templates or create custom items</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {lineItems.map((item, index) => (
                    <div
                      key={item.id}
                      className="p-3 sm:p-4 border rounded-lg space-y-3 bg-white"
                      data-testid={`line-item-${index}`}
                    >
                      {/* Mobile: Stack vertically, Desktop: Row */}
                      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                        <div className="flex-1">
                          <Input
                            value={item.name}
                            onChange={(e) => handleUpdateLineItem(item.id, { name: e.target.value })}
                            placeholder="Service name *"
                            className="font-medium h-11 sm:h-10 text-base sm:text-sm"
                            data-testid={`line-item-name-${index}`}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1 sm:w-32">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                            <Input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="0.01"
                              value={item.price}
                              onChange={(e) => handleUpdateLineItem(item.id, { price: e.target.value })}
                              className="pl-7 text-right h-11 sm:h-10 text-base sm:text-sm"
                              placeholder="0.00"
                              data-testid={`line-item-price-${index}`}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveLineItem(item.id)}
                            className="p-2.5 sm:p-2 hover:bg-red-50 rounded-lg transition-colors touch-manipulation"
                            data-testid={`remove-item-${index}`}
                          >
                            <Trash2 className="w-5 h-5 sm:w-4 sm:h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                      <Textarea
                        value={item.description}
                        onChange={(e) => handleUpdateLineItem(item.id, { description: e.target.value })}
                        placeholder="Description (optional)"
                        rows={2}
                        className="text-sm"
                        data-testid={`line-item-desc-${index}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Service Plan (Optional) */}
            <Card className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <RefreshCw className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <h2 className="font-semibold text-gray-900">Service Plan</h2>
                  <p className="text-xs sm:text-sm text-gray-500">Optional — set a recurring schedule for this client</p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer shrink-0">
                  <span className="text-sm text-gray-500">Include</span>
                  <input
                    type="checkbox"
                    checked={recurringPlan?.active ?? false}
                    onChange={(e) =>
                      setRecurringPlan((prev) =>
                        e.target.checked
                          ? { ...(prev ?? {}), active: true, interval: prev?.interval ?? 30, unit: 'days' }
                          : { ...(prev ?? {}), active: false }
                      )
                    }
                    className="h-4 w-4 accent-secondary"
                    data-testid="manual-recurring-toggle"
                  />
                </label>
              </div>

              {recurringPlan?.active && (
                <div className="space-y-4 pt-1">
                  {/* ── Service Program binding — drives planLabel + interval defaults ── */}
                  <div className="space-y-1.5">
                    <Label htmlFor="rp-program" className="text-sm">Service Program</Label>
                    <select
                      id="rp-program"
                      value={recurringPlan.servicePlanTemplateId || ''}
                      onChange={(e) => selectServiceProgram(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                      data-testid="manual-recurring-program"
                    >
                      <option value="">— Select a program —</option>
                      {servicePrograms.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                          {p.suggestedIntervalDays ? ` · every ${p.suggestedIntervalDays}d` : ''}
                        </option>
                      ))}
                    </select>
                    {recurringPlan.servicePlanTemplateId && (
                      <p className="text-xs text-gray-500" data-testid="manual-recurring-program-linked">
                        Visit sequence (products, areas, tech notes) will come from this program.
                      </p>
                    )}
                    {servicePrograms.length === 0 && (
                      <p className="text-xs text-amber-600">
                        No programs set up yet.{' '}
                        <a
                          href="/settings/service-programs"
                          className="underline hover:text-amber-700"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Create one →
                        </a>
                      </p>
                    )}
                  </div>

                  {/* Plan label */}
                  <div className="space-y-1.5">
                    <Label htmlFor="rp-manual-label" className="text-sm">Plan Label</Label>
                    <Input
                      id="rp-manual-label"
                      value={recurringPlan.planLabel ?? ''}
                      onChange={(e) =>
                        setRecurringPlan((prev) => ({ ...prev, planLabel: e.target.value }))
                      }
                      placeholder="e.g. Monthly Lawn Care Plan"
                      className="h-10"
                      data-testid="manual-recurring-label"
                    />
                  </div>

                  {/* Frequency presets */}
                  <div className="space-y-2">
                    <Label className="text-sm">Visit Frequency</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {[
                        { label: 'Monthly (30 days)',  days: 30 },
                        { label: 'Every 60 days',      days: 60 },
                        { label: 'Every 90 days',      days: 90 },
                        { label: 'Custom',             days: 'custom' },
                      ].map(({ label, days }) => {
                        const isCustomSelected = ![30, 60, 90].includes(recurringPlan.interval ?? 0);
                        const isSelected =
                          days === 'custom'
                            ? isCustomSelected
                            : recurringPlan.interval === days;
                        return (
                          <label
                            key={label}
                            className={`flex items-center gap-2 cursor-pointer p-2.5 rounded-md border text-sm transition-colors
                              ${isSelected ? 'border-secondary bg-secondary/5 font-medium' : 'border-gray-200 hover:bg-gray-50'}`}
                          >
                            <input
                              type="radio"
                              name="rp-interval-manual"
                              checked={isSelected}
                              onChange={() => {
                                if (days === 'custom') {
                                  setRecurringPlan((prev) => ({ ...prev, interval: null }));
                                } else {
                                  setRecurringPlan((prev) => ({ ...prev, interval: days }));
                                }
                              }}
                              className="accent-secondary"
                              data-testid={`manual-freq-${days}`}
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>

                    {/* Custom interval input — shown when no preset is selected */}
                    {![30, 60, 90].includes(recurringPlan.interval ?? 0) && (
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          min="1"
                          value={recurringPlan.interval ?? ''}
                          onChange={(e) =>
                            setRecurringPlan((prev) => ({
                              ...prev,
                              interval: parseInt(e.target.value, 10) || null,
                            }))
                          }
                          placeholder="e.g. 45"
                          className="w-24 h-9"
                          data-testid="manual-recurring-custom-days"
                        />
                        <span className="text-sm text-gray-500">days between visits</span>
                      </div>
                    )}
                  </div>

                  {/* Total visits */}
                  <div className="space-y-2">
                    <Label className="text-sm">Total Visits</Label>
                    <div className="flex flex-wrap items-center gap-4">
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="rp-occurrences-manual"
                          checked={!recurringPlan.totalOccurrences}
                          onChange={() =>
                            setRecurringPlan((prev) => ({ ...prev, totalOccurrences: null }))
                          }
                          className="accent-secondary"
                        />
                        Ongoing (no limit)
                      </label>
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="rp-occurrences-manual"
                          checked={!!recurringPlan.totalOccurrences}
                          onChange={() =>
                            setRecurringPlan((prev) => ({ ...prev, totalOccurrences: 12 }))
                          }
                          className="accent-secondary"
                        />
                        Limit to
                        <Input
                          type="number"
                          min="1"
                          value={recurringPlan.totalOccurrences ?? ''}
                          onChange={(e) =>
                            setRecurringPlan((prev) => ({
                              ...prev,
                              totalOccurrences: parseInt(e.target.value, 10) || null,
                            }))
                          }
                          className="w-16 h-7 text-xs px-1 inline-block"
                          data-testid="manual-recurring-occurrences"
                        />
                        visits
                      </label>
                    </div>

                    {/* Seasonal suggestion — only renders when the linked program
                        has a season window. Same helper as QuoteSummary / ServicePlanCard. */}
                    <QuoteWorkspaceSeasonalSuggestion
                      servicePrograms={servicePrograms}
                      recurringPlan={recurringPlan}
                      onUseSuggestion={(n) =>
                        setRecurringPlan((prev) => ({ ...prev, totalOccurrences: n }))
                      }
                    />
                  </div>

                  {/* Start date */}
                  <div className="space-y-1.5">
                    <Label htmlFor="rp-manual-start" className="text-sm">
                      Start Date{' '}
                      <span className="text-gray-400 font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="rp-manual-start"
                      type="date"
                      value={recurringPlan.startDate ?? ''}
                      onChange={(e) =>
                        setRecurringPlan((prev) => ({
                          ...prev,
                          startDate: e.target.value || null,
                        }))
                      }
                      className="w-48 h-9"
                      data-testid="manual-recurring-start-date"
                    />
                  </div>
                </div>
              )}
            </Card>

          {/* ── Prepay Package (left column, after Service Plan) ──────────────────── */}
          <div ref={prepayRef}>
            <Card className="p-6 space-y-4">
              {/* Header row with toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-primary" />
                  <h2 className="font-semibold text-gray-900">Prepay Package</h2>
                  <span className="text-xs text-muted-foreground">Charge client upfront for a block of visits</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-muted-foreground">Include</span>
                  <input
                    type="checkbox"
                    checked={prepayPackage?.active ?? false}
                    onChange={(e) => handlePrepayChange({ active: e.target.checked })}
                    className="h-4 w-4 accent-primary"
                    data-testid="ws-prepay-toggle"
                  />
                </label>
              </div>

              {prepayPackage?.active && (
                <div className="space-y-4">
                  {/* Package Name */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Package Name</label>
                    <input
                      type="text"
                      value={prepayPackage.packageName ?? ''}
                      onChange={(e) => handlePrepayChange({ packageName: e.target.value })}
                      placeholder="e.g. 12-Visit Annual Plan"
                      className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      data-testid="ws-prepay-name"
                    />
                  </div>

                  {/* Visits + Price on same row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-gray-700">Visits Prepaid</label>
                      <input
                        type="number"
                        min="1"
                        value={prepayPackage.visitCount ?? ''}
                        onChange={(e) => handlePrepayChange({ visitCount: parseInt(e.target.value, 10) || null })}
                        placeholder="e.g. 12"
                        className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="ws-prepay-visits"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                        Package Price ($)
                        {prepayPackage.visitCount && prepayPackage.packagePrice && !prepayPriceUserEdited && (
                          <span className="text-[10px] text-primary/60 font-normal">(auto)</span>
                        )}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={prepayPackage.packagePrice ?? ''}
                        onChange={(e) => handlePrepayChange({ packagePrice: parseFloat(e.target.value) || null })}
                        placeholder="0.00"
                        className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="ws-prepay-price"
                      />
                    </div>
                  </div>

                  {/* Live per-visit summary */}
                  {prepayPackage.visitCount > 0 && prepayPackage.packagePrice > 0 && (
                    <p className="text-xs text-muted-foreground bg-muted rounded px-3 py-2" data-testid="ws-prepay-meta">
                      ${(prepayPackage.packagePrice / prepayPackage.visitCount).toFixed(2)} per visit
                      {' · '}
                      {prepayPackage.visitCount} visits covered upfront
                      {total > 0 && !prepayPriceUserEdited && (
                        <span className="ml-1 text-primary/60">
                          (${((total - prepayPackage.packagePrice / prepayPackage.visitCount)).toFixed(2)} savings/visit vs. pay-per-visit)
                        </span>
                      )}
                    </p>
                  )}
                </div>
              )}
            </Card>
          </div>
          </div>

          {/* Right Column - Summary & Actions (Hidden on mobile, shown on desktop) */}
          <div className="hidden lg:block lg:sticky lg:top-24 h-fit">
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quote Summary</h3>

              {/* Client Preview */}
              {(clientInfo.firstName || clientInfo.lastName || clientInfo.phone) && (
                <div className="mb-4 pb-4 border-b">
                  <div className="text-sm text-gray-500 mb-1">Client</div>
                  <div className="font-medium">
                    {[clientInfo.firstName, clientInfo.lastName].filter(Boolean).join(' ') || 'No name'}
                  </div>
                  {clientInfo.phone && (
                    <div className="text-sm text-gray-600">{clientInfo.phone}</div>
                  )}
                  {clientInfo.address && (
                    <div className="text-sm text-gray-600 mt-1">
                      {clientInfo.address}, {clientInfo.city} {clientInfo.state}
                    </div>
                  )}
                </div>
              )}

              {/* Line Items Preview */}
              {lineItems.length > 0 && (
                <div className="mb-4 pb-4 border-b space-y-2">
                  <div className="text-sm text-gray-500 mb-2">Services</div>
                  {lineItems.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-gray-700 truncate mr-2">{item.name || 'Unnamed'}</span>
                      <span className="font-medium">${parseFloat(item.price || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Total — with tax breakdown when applicable */}
              {taxAmount > 0 ? (
                <div className="space-y-1.5 mb-3" data-testid="quote-tax-breakdown">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Subtotal</span>
                    <span data-testid="quote-subtotal">${total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Tax ({taxRate}%)</span>
                    <span data-testid="quote-tax-amount">${taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-lg font-bold border-t pt-2" data-testid="quote-grand-total-row">
                    <span>Total</span>
                    <span data-testid="quote-grand-total">${grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-center text-lg font-bold mb-3" data-testid="quote-grand-total-row">
                  <span>Total</span>
                  <span data-testid="quote-grand-total">${grandTotal.toFixed(2)}</span>
                </div>
              )}

              {/* Prepay discoverability prompt */}
              {total > 0 && !prepayPackage?.active && (
                <button
                  type="button"
                  onClick={handlePrepayPromptClick}
                  className="w-full text-xs text-primary/80 hover:text-primary flex items-center justify-center gap-1.5 py-1.5 mb-3 rounded-md hover:bg-primary/5 transition-colors border border-dashed border-primary/30"
                  data-testid="ws-prepay-prompt-btn"
                >
                  <Package className="h-3 w-3" />
                  Offer this as a prepaid program?
                </button>
              )}

              {/* Prepay active badge */}
              {prepayPackage?.active && prepayPackage?.packagePrice > 0 && (
                <div className="text-xs text-primary bg-primary/5 border border-primary/20 rounded-md px-3 py-2 mb-3 flex items-center gap-1.5" data-testid="ws-prepay-active-badge">
                  <Package className="h-3 w-3" />
                  Prepay: {prepayPackage.visitCount} visits · ${prepayPackage.packagePrice?.toFixed(2)}
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={actionInProgress !== null}
                  className="w-full justify-start"
                  data-testid="save-draft-btn"
                >
                  {actionInProgress === 'save' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Draft
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendSMS}
                  disabled={actionInProgress !== null || !clientInfo.phone}
                  className="w-full justify-start"
                  title={!clientInfo.phone ? 'Phone number required' : ''}
                  data-testid="send-sms-btn"
                >
                  {actionInProgress === 'sms' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send via SMS
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrint}
                  disabled={actionInProgress !== null}
                  className="w-full justify-start"
                  data-testid="print-quote-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Quote
                </Button>

                <Button
                  type="button"
                  onClick={handleBookJob}
                  disabled={actionInProgress !== null}
                  className="w-full justify-start text-white"
                  style={{ backgroundColor: 'var(--cb-orange)' }}
                  data-testid="book-job-btn"
                >
                  {actionInProgress === 'book' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CalendarCheck className="w-4 h-4 mr-2" />
                  )}
                  Approve Quote
                </Button>
              </div>

              {/* Helper text */}
              <p className="text-xs text-gray-500 mt-4 text-center">
                Quote persists on action. No auto-save.
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Bottom Action Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-20">
        {/* Expandable Summary */}
        {showMobileSummary && (
          <div className="px-4 py-3 border-b bg-gray-50 max-h-48 overflow-y-auto">
            {(clientInfo.firstName || clientInfo.lastName || clientInfo.phone) && (
              <div className="mb-2 pb-2 border-b border-gray-200">
                <div className="text-xs text-gray-500">Client</div>
                <div className="text-sm font-medium">
                  {[clientInfo.firstName, clientInfo.lastName].filter(Boolean).join(' ') || 'No name'}
                  {clientInfo.phone && <span className="text-gray-500 ml-2">{clientInfo.phone}</span>}
                </div>
              </div>
            )}
            {lineItems.length > 0 && (
              <div className="space-y-1">
                {lineItems.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-gray-700 truncate mr-2">{item.name || 'Unnamed'}</span>
                    <span className="font-medium">${parseFloat(item.price || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summary Toggle + Total */}
        <button
          type="button"
          onClick={() => setShowMobileSummary(!showMobileSummary)}
          className="w-full px-4 py-2 flex items-center justify-between bg-gray-50 border-b touch-manipulation"
        >
          <div className="flex items-center gap-2 text-sm text-gray-600">
            {showMobileSummary ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronUp className="w-4 h-4" />
            )}
            <span>{lineItems.length} item{lineItems.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="text-lg font-bold" data-testid="mobile-grand-total">${grandTotal.toFixed(2)}</div>
        </button>

        {/* Primary Actions - Always visible */}
        <div className="px-3 py-3 flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleSendSMS}
            disabled={actionInProgress !== null || !clientInfo.phone}
            className="flex-1 h-12 text-sm font-medium"
            data-testid="mobile-send-sms-btn"
          >
            {actionInProgress === 'sms' ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-1.5" />
            )}
            Send SMS
          </Button>
          <Button
            type="button"
            onClick={handleBookJob}
            disabled={actionInProgress !== null}
            className="flex-1 h-12 text-sm font-medium text-white"
            style={{ backgroundColor: 'var(--cb-orange)' }}
            data-testid="mobile-book-job-btn"
          >
            {actionInProgress === 'book' ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <CalendarCheck className="w-4 h-4 mr-1.5" />
            )}
            Approve Quote
          </Button>
        </div>
        <div className="px-3 pb-3 flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSaveDraft}
            disabled={actionInProgress !== null}
            className="flex-1 h-10 text-xs"
            data-testid="mobile-save-draft-btn"
          >
            {actionInProgress === 'save' ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5 mr-1" />
            )}
            Save Draft
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handlePrint}
            disabled={actionInProgress !== null}
            className="flex-1 h-10 text-xs"
            data-testid="mobile-print-btn"
          >
            <Printer className="w-3.5 h-3.5 mr-1" />
            Print
          </Button>
        </div>
      </div>
    </div>

    {/* ── Service Plan Activation Confirmation Dialog ─────────────────────── */}
    {recurringPlan?.active && (
      <Dialog open={showPlanConfirm} onOpenChange={setShowPlanConfirm}>
        <DialogContent className="sm:max-w-md" data-testid="plan-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-5 w-5 text-[var(--cb-orange)]" />
              Confirm Service Plan Activation
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500 pt-1">
              Approving this quote will enroll this client in a recurring service plan.
            </DialogDescription>
          </DialogHeader>

          {/* Plan summary card */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3 my-1">
            {recurringPlan.servicePlanTemplateId && (() => {
              const prog = servicePrograms.find(p => p.id === recurringPlan.servicePlanTemplateId);
              if (!prog) return null;
              return (
                <div
                  className="flex items-center gap-2"
                  data-testid="plan-confirm-linked-program"
                >
                  <Package className="h-4 w-4 text-[var(--cb-orange)] shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 leading-none">Linked Program</p>
                    <p className="text-sm font-semibold text-gray-900">{prog.name}</p>
                  </div>
                </div>
              );
            })()}
            {recurringPlan.planLabel && (
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 leading-none">Plan name</p>
                  <p className="text-sm font-semibold text-gray-900">{recurringPlan.planLabel}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Repeat className="h-4 w-4 text-gray-400 shrink-0" />
              <div>
                <p className="text-xs text-gray-500 leading-none">Frequency</p>
                <p className="text-sm font-medium text-gray-900">
                  Every {recurringPlan.interval} days
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-gray-400 shrink-0" />
              <div>
                <p className="text-xs text-gray-500 leading-none">Duration</p>
                <p className="text-sm font-medium text-gray-900">
                  {recurringPlan.totalOccurrences
                    ? `${recurringPlan.totalOccurrences} visits`
                    : 'Ongoing (no end date)'}
                </p>
              </div>
            </div>
            {recurringPlan.startDate && (
              <div className="flex items-center gap-2">
                <CalendarCheck className="h-4 w-4 text-gray-400 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 leading-none">First visit</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(recurringPlan.startDate + 'T00:00:00').toLocaleDateString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-400 leading-relaxed">
            A first visit task will be added to the Scheduler automatically. Future visits will be projected in Forecast and seeded after each completion.
          </p>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPlanConfirm(false)}
              data-testid="plan-confirm-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => { setShowPlanConfirm(false); executeBookJob(); }}
              disabled={actionInProgress !== null}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              data-testid="plan-confirm-approve-btn"
            >
              {actionInProgress === 'book' ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-1.5" />
              )}
              Confirm & Activate Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};

export default QuoteWorkspace;

// ─── Seasonal-suggestion inline component (Manual Quote context) ───────────
// Mirrors the UX in QuoteSummary.jsx and ServicePlanCard.jsx. Pure UI assist —
// the recurrence engine never reads this; operator override always wins.
function QuoteWorkspaceSeasonalSuggestion({ servicePrograms, recurringPlan, onUseSuggestion }) {
  const program = useMemo(() => {
    const id = recurringPlan?.servicePlanTemplateId;
    if (!id) return null;
    return (servicePrograms || []).find((p) => p.id === id) || null;
  }, [servicePrograms, recurringPlan?.servicePlanTemplateId]);

  const suggestion = useMemo(() => {
    if (!program) return null;
    if (!program.seasonStartMonthDay && !program.seasonEndMonthDay) return null;
    return calculateSuggestedOccurrences({
      startDate: recurringPlan?.startDate || undefined,
      intervalDays: parseInt(recurringPlan?.interval, 10),
      seasonStartMonthDay: program.seasonStartMonthDay || null,
      seasonEndMonthDay:   program.seasonEndMonthDay   || null,
    });
  }, [program, recurringPlan?.startDate, recurringPlan?.interval]);

  if (!suggestion) return null;

  const intervalNum = parseInt(recurringPlan?.interval, 10) || 14;
  const endLabel = formatSeasonEndShort(program.seasonEndMonthDay || '12-31');

  if (suggestion.reason === 'past_season') {
    return (
      <div
        className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2"
        data-testid="ws-seasonal-suggestion-past"
      >
        <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">Start date is past the season end.</div>
          <div className="text-amber-700">
            This program&apos;s season ends {endLabel}. Pick an earlier start date
            or book under next season.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
      data-testid="ws-seasonal-suggestion"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-700" />
        <div className="flex-1">
          <div className="font-semibold">
            Suggested remaining visits:{' '}
            <span data-testid="ws-seasonal-suggestion-count">{suggestion.count}</span>
          </div>
          <div className="text-emerald-700 leading-tight mt-0.5">
            Based on a {intervalNum}-day interval and a season ending{' '}
            <span className="font-medium">{endLabel}</span>
            {suggestion.reason === 'before_season' && (
              <> (counted from the season start)</>
            )}
            .
          </div>
          <div className="text-[10px] text-emerald-600 mt-0.5 italic">
            Suggestion only — operator override always wins.
          </div>
        </div>
        <button
          type="button"
          onClick={() => onUseSuggestion(suggestion.count)}
          className="text-[11px] font-semibold rounded border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-100 px-2 py-1 transition-colors flex-shrink-0"
          data-testid="ws-seasonal-use-btn"
        >
          Use {suggestion.count}
        </button>
      </div>
    </div>
  );
}

